/**
 * Option type for tag dropdowns
 */
export type TagOption = {
  value: string;
  label?: string;
};

const ENCODED_TAG_VALUE_PREFIX = '$fluents:tag:';

export const isEncodedTagValue = (value: string): boolean => {
  if (!value) {
    return false;
  }
  return value.startsWith(ENCODED_TAG_VALUE_PREFIX);
};

export const decodeTagValue = (value: string): string => {
  if (!isEncodedTagValue(value)) {
    return value;
  }
  return value.slice(ENCODED_TAG_VALUE_PREFIX.length);
};

/**
 * Sanitizes a single tag value
 *
 * @param value - Tag value to sanitize
 * @returns Sanitized tag value or empty string if invalid
 */
export function sanitizeTagValue(value: string): string {
  if (!value || typeof value !== 'string') {
    return '';
  }
  let cleaned = value.toLowerCase().trim();
  cleaned = cleaned.replace(/[^a-z0-9_-]/g, '');
  return cleaned;
}

/**
 * Builds tag options from an array of tag strings
 *
 * @param tags - Array of tag strings
 * @returns Array of tag options
 */
export function buildTagOptions(tags: string[]): TagOption[] {
  return tags.map((tag) => ({
    value: tag,
    label: tag,
  }));
}

/**
 * Builds encoded tag options (with encoded values and human-readable labels)
 *
 * @param tags - Array of tag strings
 * @returns Array of encoded tag options
 */
export function buildEncodedTagOptions(tags: string[]): TagOption[] {
  return tags.map((tag) => ({
    value: `${ENCODED_TAG_VALUE_PREFIX}${tag}`,
    label: tag,
  }));
}

/**
 * Builds tag options from an array of tag strings with optional query filtering
 *
 * @param tags - Array of tag strings
 * @param query - Optional search query to filter tags
 * @returns Array of tag options
 */
export function buildTagOptionsWithQuery(
  tags: string[],
  query: string = '',
): TagOption[] {
  let options = tags.map((tag) => ({
    value: tag,
    label: tag,
  }));

  // Filter by query if provided
  if (query) {
    const lowerQuery = query.toLowerCase();
    options = options.filter((opt) =>
      opt.value.toLowerCase().includes(lowerQuery),
    );

    // Add the query as a new option if it's valid and not already present
    const sanitizedQuery = sanitizeTagValue(query);
    if (
      sanitizedQuery &&
      !options.some((opt) => opt.value === sanitizedQuery)
    ) {
      options = [{ value: sanitizedQuery, label: sanitizedQuery }, ...options];
    }
  }

  return options;
}

/**
 * Merges tag options with selected values, ensuring selected values are included
 *
 * @param options - Available tag options
 * @param selectedValues - Array of selected tag values
 * @returns Merged array of tag options
 */
export function mergeTagOptions(
  options: TagOption[],
  selectedValues: string[],
): TagOption[] {
  const optionValues = new Set(options.map((opt) => opt.value));
  const missingOptions = selectedValues
    .filter((value) => !optionValues.has(value))
    .map((value) => ({ value, label: value }));

  return [...missingOptions, ...options];
}
